import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import updateLocale from "dayjs/plugin/updateLocale";
import weekday from "dayjs/plugin/weekday";
import utc from "dayjs/plugin/utc";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";

import { Provider } from "react-redux";
import { RouterProvider } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import styled from "styled-components";

import { GlobalStyle } from "./GlobalStyle";
import ThemeProvider from "./ThemeContext";
import { onAppStart } from "./axios/onAppStart";
import { Flex } from "./components/Flex";
import { useLemon } from "./hooks/useLemon";
import store from "./redux/store";
import {
  getStateLoggedInUserId,
  getTokenUserId,
  isImpersonationTab,
} from "./redux/store-utils";
import router from "./routes/router/Router";
import { AntdConfig } from "./theme/AntdConfig";
import { UnsavedChangesWarningModalProvider } from "./routes/campaign/components/unsaved-changes-warning-modal/unsaved-changes-warning-modal-context";
import { DrawersContextProvider } from "./routes/tasks/drawers-context";
import { ErroBoundary } from "./components/ErrorBoundary";
import { AtiraFormContextProvider } from "./routes/form/context/form-context";
import { SocketConnection } from "./components/shared/SocketConnection";

dayjs.extend(weekday);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(updateLocale);
dayjs.updateLocale("en", { weekStart: 1 });
dayjs.extend(utc);

const RootContainer = styled(Flex)`
  flex-direction: column;
  height: 100%;
  min-height: 100%;
`;

function App() {
  // if (process.env.NODE_ENV === 'production') {
  //   console.log = () => {};
  //   console.error = () => {};
  //   console.debug = () => {};
  // }

  useLemon();
  useEffect(() => {
    // Do not run onAppStart when impersonating, it already does it when it is ready
    if (!window.location.pathname.startsWith("/impersonate")) {
      onAppStart();
    }

    const timer = setTimeout(() => {
      // See private router for more info
      window.localStorage.removeItem("atiraspace-chunk-load-error-reloaded");
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // localStorage is shared by every tab, but each tab keeps its own in-memory
    // redux store and never hears about writes made elsewhere. Logging in as a
    // different account in a second tab therefore swaps the token out from
    // under this one: the header keeps rendering the old account while every
    // request comes back with the new account's data.
    //
    // Impersonation tabs authenticate from sessionStorage, which is private to
    // the tab, so they are unaffected.
    if (isImpersonationTab()) {
      return;
    }

    const onStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) {
        return;
      }

      // A null key means localStorage.clear() - the token may well be one of
      // the things that just disappeared, so we cannot filter that event out.
      if (
        event.key !== null &&
        event.key !== process.env.REACT_APP_LOCAL_STORAGE_TOKEN_KEY
      ) {
        return;
      }

      // Decide on identity rather than on the event's payload: whether the
      // token string changed is not the question, whether it now belongs to a
      // different account is. This also covers logout elsewhere, where the
      // token is removed and there is no new account at all.
      if (getTokenUserId() === getStateLoggedInUserId()) {
        return;
      }

      // if something isn't right, simply reload and let the boot up logic resolve itself
      window.location.reload();
    };

    window.addEventListener("storage", onStorage);

    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErroBoundary error={error} resetError={resetError} />
      )}
    >
      <GlobalStyle />
      <Provider store={store.store}>
        <PersistGate persistor={store.persistor} loading={null}>
          <RootContainer>
            <ThemeProvider>
              <AntdConfig>
                <UnsavedChangesWarningModalProvider>
                  <DrawersContextProvider>
                    <AtiraFormContextProvider>
                      <RouterProvider router={router} />
                      <SocketConnection />
                    </AtiraFormContextProvider>
                  </DrawersContextProvider>
                </UnsavedChangesWarningModalProvider>
              </AntdConfig>
            </ThemeProvider>
          </RootContainer>
        </PersistGate>
      </Provider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
